import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Chip, CircularProgress, Grid, MenuItem, Stack,
  TextField, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, LinearProgress,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TimerIcon from '@mui/icons-material/Timer';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import config from '../../../config';

// HR Dashboard — single composite call gives the KPI overlay for the
// whole module. Quick links shortcut to deeper drill-down pages.
export default function HrDashboard() {
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const companyId = company?.id;
  const navigate = useNavigate();
  const now = new Date();

  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/Dashboard/${companyId}?month=${month}&year=${year}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error();
      setData(await res.json());
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, month, year]);

  const inr = (v) =>
    '₹ ' + Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

  const Tile = ({ icon, label, value, color, sub, onClick }) => (
    <Card
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        borderLeft: `4px solid ${color}`,
        p: 1.5,
        transition: 'transform 0.15s',
        '&:hover': onClick ? { transform: 'translateY(-2px)' } : {},
      }}>
      <Stack direction='row' spacing={1.5} alignItems='center'>
        <Box
          sx={{
            width: 38, height: 38, borderRadius: 1.5,
            bgcolor: `${color}22`, color, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.3 }}>
            {label}
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
            {value}
          </Typography>
          {sub && <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>{sub}</Typography>}
        </Box>
      </Stack>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#F1F5F9', minHeight: '100%' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent='space-between' sx={{ mb: 2 }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: '#1A56DB', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <GroupsIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.2rem' }}>
              HR Dashboard
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#64748B' }}>
              {data?.today ? new Date(data.today).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
            </Typography>
          </Box>
        </Stack>
        <Stack direction='row' spacing={1.5}>
          <TextField select size='small' label='Month' value={month}
            onChange={(e) => setMonth(e.target.value)}
            sx={{ bgcolor: '#fff', minWidth: 140 }}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <MenuItem key={m} value={m}>
                {new Date(2000, m - 1, 1).toLocaleString('en-IN', { month: 'long' })}
              </MenuItem>
            ))}
          </TextField>
          <TextField type='number' size='small' label='Year' value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            sx={{ bgcolor: '#fff', width: 110 }} />
          <Button variant='outlined' onClick={() => navigate('/admin/Hr/Reports')}
            endIcon={<OpenInNewIcon />}>
            Reports
          </Button>
        </Stack>
      </Stack>

      {loading || !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Today's tiles */}
          <Typography sx={{ fontWeight: 700, color: '#475569', mb: 1, fontSize: '0.85rem' }}>
            TODAY
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2.4}>
              <Tile icon={<GroupsIcon />} label='Active Staff'
                value={`${data.activeEmployees} / ${data.totalEmployees}`}
                color='#1A56DB'
                onClick={() => navigate('/admin/Hr/Employees')} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Tile icon={<EventAvailableIcon />} label='Present'
                value={data.presentToday}
                color='#16a34a'
                onClick={() => navigate('/admin/Hr/Attendance')} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Tile icon={<PersonOffIcon />} label='Absent'
                value={data.absentToday} color='#dc2626' />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Tile icon={<EventBusyIcon />} label='On Leave'
                value={data.onLeaveToday} color='#2563eb'
                onClick={() => navigate('/admin/Hr/LeaveRequests')} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Tile icon={<HourglassEmptyIcon />} label='Unmarked'
                value={data.unmarkedToday} color='#f59e0b'
                onClick={() => navigate('/admin/Hr/Attendance')}
                sub={data.weeklyOffToday + data.holidayToday > 0
                  ? `+${data.weeklyOffToday + data.holidayToday} off/holiday`
                  : null} />
            </Grid>
          </Grid>

          {/* Month payroll tiles */}
          <Typography sx={{ fontWeight: 700, color: '#475569', mb: 1, fontSize: '0.85rem' }}>
            {new Date(year, month - 1, 1).toLocaleString('en-IN', { month: 'long' })} {year} · PAYROLL
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Tile icon={<ReceiptIcon />} label='Gross payroll'
                value={inr(data.monthGrossPayroll)} color='#16a34a'
                sub={`${data.monthPayslipsGenerated} payslip(s) generated`}
                onClick={() => navigate('/admin/Hr/Payroll')} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Tile icon={<PaymentIcon />} label='Net payroll'
                value={inr(data.monthNetPayroll)} color='#1A56DB'
                sub={`Paid: ${inr(data.monthPaid)}`}
                onClick={() => navigate('/admin/Hr/SalaryPayments')} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Tile icon={<TimerIcon />} label='Overtime'
                value={inr(data.monthOvertimeAmount)} color='#f97316'
                sub={`${data.monthOvertimeRows} entry(s)`}
                onClick={() => navigate('/admin/Hr/Overtime')} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Tile icon={<HourglassEmptyIcon />} label='Pending payments'
                value={inr(data.monthPending)} color='#dc2626'
                sub={`${data.monthPayslipsGenerated - data.monthPayslipsPaid} unpaid`} />
            </Grid>
          </Grid>

          {/* Outstanding section */}
          <Typography sx={{ fontWeight: 700, color: '#475569', mb: 1, fontSize: '0.85rem' }}>
            OUTSTANDING
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Tile icon={<PaymentIcon />} label='Pending advances'
                value={inr(data.pendingAdvancesAmount)} color='#6366f1'
                sub={`${data.pendingAdvancesCount} active`}
                onClick={() => navigate('/admin/Hr/AdvanceSalary')} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Tile icon={<AccountBalanceIcon />} label='Loans outstanding'
                value={inr(data.loanOutstandingAmount)} color='#0d9488'
                sub={`${data.activeLoansCount} active`}
                onClick={() => navigate('/admin/Hr/Loans')} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Tile icon={<CheckroomIcon />} label='Uniform deposit held'
                value={inr(data.uniformDepositHeld)} color='#475569'
                onClick={() => navigate('/admin/Hr/UniformDeposit')} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Tile icon={<HourglassEmptyIcon />} label='Upcoming payouts'
                value={inr(data.upcomingPayments)} color='#a855f7'
                sub='Processed but unpaid' />
            </Grid>
          </Grid>

          {/* Department cost breakdown */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.5 }}>
                <Typography sx={{ fontWeight: 700 }}>
                  Department Cost Breakdown
                </Typography>
                <Chip size='small' label={`${data.departmentBreakdown.length} dept`}
                  sx={{ bgcolor: '#f1f5f9' }} />
              </Stack>
              {data.departmentBreakdown.length === 0 ? (
                <Typography sx={{ color: '#94a3b8', py: 2, textAlign: 'center' }}>
                  No payroll for this month yet.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align='right'>Staff</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align='right'>Gross</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align='right'>Deductions</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align='right'>Net</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align='right'>OT</TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>Share</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.departmentBreakdown.map((r, i) => {
                        const pct = data.monthNetPayroll > 0
                          ? (Number(r.netSalary) / data.monthNetPayroll) * 100
                          : 0;
                        const colors = ['#1A56DB', '#16a34a', '#f59e0b', '#dc2626', '#8b5cf6', '#0891b2', '#ec4899'];
                        return (
                          <TableRow key={r.departmentId || `un-${i}`} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{r.departmentName}</TableCell>
                            <TableCell align='right'>{r.employeeCount}</TableCell>
                            <TableCell align='right'>{inr(r.totalEarnings)}</TableCell>
                            <TableCell align='right' sx={{ color: '#dc2626' }}>
                              {inr(r.totalDeductions)}
                            </TableCell>
                            <TableCell align='right' sx={{ fontWeight: 700 }}>
                              {inr(r.netSalary)}
                            </TableCell>
                            <TableCell align='right' sx={{ color: '#f97316' }}>
                              {inr(r.overtimeAmount)}
                            </TableCell>
                            <TableCell>
                              <Stack direction='row' alignItems='center' spacing={1}>
                                <Box sx={{ flex: 1 }}>
                                  <LinearProgress variant='determinate' value={pct}
                                    sx={{
                                      height: 6, borderRadius: 3, bgcolor: '#f1f5f9',
                                      '& .MuiLinearProgress-bar': {
                                        bgcolor: colors[i % colors.length],
                                      },
                                    }} />
                                </Box>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, minWidth: 38 }}>
                                  {pct.toFixed(1)}%
                                </Typography>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
